import React, { useMemo } from "react";
import { Box, HStack, Icon, Text } from "@chakra-ui/react";
import { FileArtifactProto } from "build-ts/dataframe";
import { FileType } from "build-ts/common";
import { AppIcons } from "constants/icons";
import ArtifactPanelHeader from "modules/artifacts/ArtifactPanelHeader";
import { DownloadMenu } from "modules/common/DownloadMenu";
import {
  copyRawTableToClipboard,
  downloadRawTableAsExcel,
  extractFormattedDataFromPandasDf,
  getPandasDfRowColCount,
} from "modules/artifacts/dataframe/utils/pandasTableUtils";
import PandasTable from "modules/artifacts/dataframe/components/PandasTable";

const SCROLL_SX = {
  "&::-webkit-scrollbar": { width: "4px" },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: "var(--chakra-colors-gray-300)", borderRadius: "2px" },
};

export interface FileArtifactProps {
  data: FileArtifactProto;
  isExpanded?: boolean;
  onExpandToggle?: () => void;
}

/**
 * Pure UI component for FILE_ARTIFACT artifacts.
 * Routes to a type-specific content view (CSV table today; more file types later).
 */
const FileArtifact: React.FC<FileArtifactProps> = ({ data, isExpanded, onExpandToggle }) => {
  const displayName = data.header?.name ?? "Uploaded File";
  const pandasDf = data.pandas_dataframe ?? null;
  const isCsv = data.file_type === FileType.CSV && !!pandasDf;

  const { rows: numberOfRows, columns: numberOfColumns } = useMemo(
    () => getPandasDfRowColCount(pandasDf ?? undefined),
    [pandasDf]
  );

  const headerActions = (
    <>
      {isCsv && numberOfRows > 0 && numberOfColumns > 0 && (
        <Text fontSize="xs" color="gray.700" mx={2} mb={0}>
          {numberOfColumns} {numberOfColumns > 1 ? "cols" : "col"}, {numberOfRows} {numberOfRows > 1 ? "rows" : "row"}
        </Text>
      )}

      {isCsv && pandasDf?.data_json && (
        <DownloadMenu
          ariaLabel="Download"
          items={[
            {
              label: "Copy to clipboard",
              icon: "copy",
              onClick: () => copyRawTableToClipboard(extractFormattedDataFromPandasDf(pandasDf)),
            },
            {
              label: "Download Excel file",
              icon: "fileExcel",
              onClick: () =>
                downloadRawTableAsExcel(
                  extractFormattedDataFromPandasDf(pandasDf),
                  `${pandasDf.name ?? displayName}.xlsx`
                ),
            },
          ]}
        />
      )}
    </>
  );

  const tablePillName = pandasDf?.name ?? displayName;

  const renderCsvTable = () => {
    if (!pandasDf?.data_json) {
      return (
        <Box px={4} py={6} textAlign="center">
          <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
            Nothing to show. Please try again...
          </Text>
        </Box>
      );
    }

    return (
      <>
        <HStack
          spacing={1.5}
          px={2}
          py={1.5}
          borderBottom="1px solid"
          borderColor="gray.100"
          bg="white"
          flexWrap="wrap"
          position="sticky"
          top={0}
          zIndex={2}
          boxShadow="sm"
        >
          <Box
            display="inline-flex"
            alignItems="center"
            px={3}
            py={1}
            borderRadius="md"
            border="2px solid"
            borderColor="purple.600"
            bg="white"
            color="purple.700"
            fontSize="xs"
            fontWeight="700"
          >
            <HStack as="span" spacing={1.5} align="center">
              <Icon as={AppIcons.dataframe} boxSize={3} color="currentColor" flexShrink={0} aria-hidden />
              <Text as="span" fontSize="xs" lineHeight="1.2" mb={0} isTruncated>
                {tablePillName}
              </Text>
            </HStack>
          </Box>
        </HStack>
        <PandasTable df={pandasDf} />
      </>
    );
  };

  const renderContent = () => {
    if (isCsv) {
      return renderCsvTable();
    }

    return (
      <Box px={4} py={6} textAlign="center">
        <Text fontSize="sm" color="gray.400" fontStyle="italic" mb={0}>
          Preview is not available for this file type yet.
        </Text>
      </Box>
    );
  };

  return (
    <Box position="relative" display="flex" flexDirection="column" width="100%" flex="1 1 0" minH={0} overflow="hidden">
      <ArtifactPanelHeader
        icon={AppIcons.file}
        iconBg="white"
        iconColor="gray.600"
        name={displayName}
        typeLabel="File"
        actions={headerActions}
        isExpanded={isExpanded}
        onExpandToggle={onExpandToggle}
      />

      <Box
        flex="1 1 0"
        minH={0}
        display="flex"
        flexDirection="column"
        overflowY="auto"
        overflowX="hidden"
        onWheel={e => e.stopPropagation()}
        sx={SCROLL_SX}
      >
        {renderContent()}
      </Box>
    </Box>
  );
};

export default FileArtifact;
